import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../../../../core/models/static/routes/app-routes";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {CircularProgress, createFilterOptions, Paper, SvgIcon, TextField} from "@mui/material";
import {ReactComponent as RemoveIcon} from "../../../../../../../../../assets/images/remove.svg";
import {Edit, Remove} from "@mui/icons-material";
import {ProductTypes} from "../../../../../../../../../core/constants/enums";
import {OutlinedInput} from "../../../../../../../../components/base/input/outlined_input";
import classnames from "classnames";
import SaveOrRollback from "../../../../../../../../components/app-specific/save-or-rollback";
import {Row} from "reactstrap";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import Chips, {ChipsTypes} from "../../../../../../../../components/base/chips/chips";
import {OutlinedAutoComplete} from "../../../../../../../../components/base/auto_complete/outlined_auto_complete";
import {SalesOrderInformationViewProductsDataGridColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {deepCopy} from "../../../../../../../../../core/services/utils/utils";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import ProductInformationPopover from "../../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {useHistory, useParams} from "react-router-dom";
import {ReactComponent as EnterIcon} from "../../../../../../../../../assets/images/products/enter.svg";
import ToastService from "../../../../../../../../../core/services/toast";
import EnvService from "../../../../../../../../../core/services/env";

const dataGridSaveConfig = {
    name: 'SalesOrderInformationViewProductsSectionDataGrid',
    version: 3,
}

const SalesOrderInformationViewProductsSection = ({
                                                      order,
                                                      setOrder,
                                                      orderLoading,
                                                      setSubmittingDisabled,
                                                  }) => {
    const params = useParams();
    const history = useHistory();
    const [updating, setUpdating] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const [productAvailabilities, setProductAvailabilities] = useState({});
    const productAvailabilitiesRef = useRef(productAvailabilities);
    productAvailabilitiesRef.current = productAvailabilities;

    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const customerId = useMemo(() => Number(params.customerId), [params.customerId])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(
                !order?.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders
                    ? [{
                        name: SalesOrderInformationViewProductsDataGridColumnNames.remove,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        pinned: true,
                        sortable: false,
                        pinnedToggleable: false,
                        resizable: false,
                        visibilityToggleable: false,
                        width: 0,
                    }]
                    : []
            ),
            {
                title: "Part No",
                name: SalesOrderInformationViewProductsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
            },
            {
                title: "Warehouse",
                name: SalesOrderInformationViewProductsDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
            },
            {
                title: "Description",
                name: SalesOrderInformationViewProductsDataGridColumnNames.description,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 250,
            },
            {
                title: "Discount",
                name: SalesOrderInformationViewProductsDataGridColumnNames.discount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 120,
            },
            {
                title: "Ship Quantity",
                name: SalesOrderInformationViewProductsDataGridColumnNames.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: "Back-order Quantity",
                name: SalesOrderInformationViewProductsDataGridColumnNames.backOrderQuantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: "Unit Price",
                name: SalesOrderInformationViewProductsDataGridColumnNames.unitPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Total Price",
                name: SalesOrderInformationViewProductsDataGridColumnNames.totalPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            ...(
                !order?.isClosed && operatorAccessLevels.packages.sales.manageSalesOrders
                    ? [{
                        title: "Available Quantity",
                        name: SalesOrderInformationViewProductsDataGridColumnNames.availableQuantity,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        sortable: false,
                        width: 150,
                    }]
                    : []
            ),
            {
                title: "Serials / Committed Quantities",
                name: SalesOrderInformationViewProductsDataGridColumnNames.committedQuantity,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 350,
            },
            ...(EnvService.DEV_showOrderProductComments
                ? [{
                    title: "Comment",
                    name: SalesOrderInformationViewProductsDataGridColumnNames.comment,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 220,
                }]
                : []),
        ], [operatorAccessLevels.packages.sales.manageSalesOrders, order?.isClosed])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**
     * Fetches the available quantities of order products from the api.
     * - sets the items' availability in the state.
     * @type {(partNos: any) => Promise<void>}
     */
    const getProductsAvailabilities = useCallback(async (partNos) => {
        if (!partNos || !partNos.length)
            return;

        setProductAvailabilities(prevState => {
            for (const partNo of partNos) {
                prevState[partNo] = {
                    ...(prevState[partNo] ?? {}),
                    loading: true,
                }
            }
            return {...prevState};
        })
        const response = await Api.getProductAvailabilitiesV2({
            partNumbers: partNos,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProductAvailabilities(prevState => {
                for (const partNo of partNos) {
                    const data = response.data?.find(e => e.partNumber === partNo);
                    prevState[partNo] = {
                        ...(prevState[partNo] ?? {}),
                        availability: data?.localWarehouses ?? [],
                    }
                }
                return {...prevState};
            })
        }
        setProductAvailabilities(prevState => {
            for (const partNo of partNos) {
                prevState[partNo] = {
                    ...(prevState[partNo] ?? {}),
                    loading: false,
                }
            }
            return {...prevState};
        })
    }, [isMounted])

    /**
     * With each change in the [items] list of the [order] prop value:
     * - determines which one of the availabilities must be removed and which ones must be added.
     */
    useEffect(() => {
        const partNos = Array.from(new Set(order?.items?.map(e => e.partNo) ?? []));
        const _productAvailabilities = deepCopy(productAvailabilitiesRef.current);

        // remove the availabilities of removed products
        for (const partNo of Object.keys(_productAvailabilities ?? {})) {
            if (!partNos.includes(partNo)) {
                // must be removed
                delete _productAvailabilities[partNo];
            }
        }

        const newPartNos = partNos.filter(partNo => _productAvailabilities[partNo] === undefined);
        if (newPartNos.length)
            getProductsAvailabilities(newPartNos).then();
    }, [getProductsAvailabilities, order?.items])

    /**
     * Removes an item from this order by calling the api.
     *
     * - if the result of the api is successful, removes the item from the list.
     * - if the order is deleted after the removal, navigates the user back to the list view
     * @type {(item: any) => Promise<void>}
     */
    const removePartFromOrder = useCallback(async (item) => {
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            partNumbersRemoveList: [item.partNo]
        };
        setUpdating(true);
        setSubmittingDisabled(true);
        const response = await Api.updateSalesOrderItems(forApi);
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
        if (!response?.resultFlag)
            return;
        setOrder(response.data);
        if (response.data.isDeleted) {
            // for (const item of order?.items ?? []) {
            //     // We have to remove all the records of the dimensions too.
            //     void Api.deleteOrderDimension({
            //         orderId: orderId,
            //         orderPartId: item.id,
            //     })
            // }
            if (order?.isBackOrder) {
                history.replace(routeFunctions.customer.sales.backOrder.base(customerId));
            } else {
                history.replace(routeFunctions.customer.sales.salesOrder.base(customerId));
            }
        } else {
            // // We have to remove the record of the dimensions too.
            // void Api.deleteOrderDimension({
            //     orderId: orderId,
            //     orderPartId: item.id,
            // })
        }
    }, [customerId, orderId, setSubmittingDisabled, isMounted, setOrder, order?.isBackOrder, history])

    /**
     * Updates the quantity of an item for the current order by calling the api.
     *
     * - if the result of the api is successful then updates the list.
     * @type {(item: any, newValue: number) => Promise<boolean>}
     */
    const updateQuantity = useCallback(async (item, newValue) => {
        setUpdating(true);
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: [
                {
                    partNo: item.partNo,
                    finalCount: operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(newValue) : parseInt(newValue),
                }
            ]
        };
        setSubmittingDisabled(true);
        const response = await Api.updateSalesOrderItems(forApi);
        if (!isMounted())
            return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        setUpdating(false);
        return response?.resultFlag;
    }, [customerId, orderId, setSubmittingDisabled, isMounted, setOrder, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    /**
     * Changes the backorder quantity of the item by calling the api,
     *
     * - if the result of the api is successful, sets the correct quantities of the item
     * @type {(item: any, newValue: number) => Promise<boolean>}
     */
    const updateBackorderQuantity = useCallback(async (item, newValue) => {
        const forApi = {
            customerId: customerId,
            saleOrderId: orderId,
            partNumber: item.partNo,
            backOrderQuantity: Number(newValue),
        };
        setUpdating(true);
        setSubmittingDisabled(true);
        let response;
        if (!item.isSerialized) {
            forApi['commitQuantity'] = Number(item.committedQty);
            response = await Api.updateSalesOrderCommittedQuantityOfPart(forApi);
        } else {
            forApi['serialNumbers'] = item.serials;
            response = await Api.updateSalesOrderPartSerialNumbers(forApi);
        }
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data)
        }
        return response?.resultFlag;
    }, [customerId, orderId, setSubmittingDisabled, isMounted, setOrder])

    /**
     * Updates the serial numbers of the provided item.
     *
     * - if the serialNo exists in the list of serialNos of the item, removes it, otherwise adds it.
     * - adjusts the backorder quantity of item as well.
     * @type {(item: any, serialNo: any) => Promise<boolean>}
     */
    const updateItemSerialNumbers = useCallback(async (item, serialNo) => {
        let newSerials;
        let markedForRemoval;
        if (item.serials?.some(e => e.serialNumber === serialNo.serialNumber)) {
            // remove
            newSerials = item.serials?.filter((e) => e.serialNumber !== serialNo.serialNumber) ?? [];
            markedForRemoval = true;
        } else {
            //add
            newSerials = [...(item.serials ?? []), serialNo];
            markedForRemoval = false;
        }
        const orderQty = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(item.orderQty) : parseInt(item.orderQty);
        const isNegative = orderQty < 0;
        const forApi = {
            customerId: customerId,
            saleOrderId: orderId,
            partNumber: item.partNo,
            backOrderQuantity: isNegative
                ? 0
                : Math.max(0, orderQty - (newSerials?.length ?? 0)),
            serialNumbers: newSerials,
        };
        setUpdating(true);
        setSubmittingDisabled(true);
        const response = await Api.updateSalesOrderPartSerialNumbers(forApi);
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
            if (markedForRemoval) {
                //     // We have to remove the record of the dimensions too.
                //     void Api.deleteOrderDimension({
                //         orderId: orderId,
                //         orderPartId: item.id,
                //         serialNumber: serialNo.serialNumber,
                //     })
            }
        }
        return response?.resultFlag;
    }, [customerId, orderId, setSubmittingDisabled, isMounted, setOrder, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    /**
     * Updates the committed quantity of the provided item.
     *
     * - if the serialNo exists in the list of serialNos of the item, removes it, otherwise adds it.
     * - adjusts the backorder quantity of item as well.
     * @type {(item: any, quantity: any) => Promise<boolean>}
     */
    const updateItemCommittedQuantity = useCallback(async (item, quantity) => {
        const orderQuantity = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(item.orderQty) : parseInt(item.orderQty);
        const isNegative = orderQuantity < 0;
        const committedQuantity = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(quantity) : parseInt(quantity);
        const backorderValue = Math.abs(orderQuantity - committedQuantity);
        const forApi = {
            customerId: customerId,
            saleOrderId: orderId,
            partNumber: item.partNo,
            commitQuantity: committedQuantity,
            backOrderQuantity: isNegative ? 0 : backorderValue,
        };
        setUpdating(true);
        setSubmittingDisabled(true);
        const response = await Api.updateSalesOrderCommittedQuantityOfPart(forApi);
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        return response?.resultFlag;
    }, [customerId, orderId, setSubmittingDisabled, isMounted, setOrder, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    /**
     * Updates the comment of a sales order line item.
     * @type {(item: any, newValue: string) => Promise<boolean|undefined>}
     */
    const updateItemComment = useCallback(async (item, newValue) => {
        setUpdating(true);
        setSubmittingDisabled(true);
        const response = await Api.updateSalesOrderItemComment({
            customerId,
            saleOrderId: orderId,
            partNo: item.partNo,
            orderPartId: item.id,
            comment: newValue?.trim() ?? '',
        });
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        return response?.resultFlag;
    }, [customerId, orderId, setSubmittingDisabled, isMounted, setOrder])

    /**
     * Gets users confirmation before removing the current order item.
     * @type {(item: any) => void}
     */
    const getRemoveConfirmation = useCallback((item) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove {item?.partNo ?? ""} from this order?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removePartFromOrder(item),
        );
    }, [removePartFromOrder])

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName].
     * @type {(_: any, newValue: any, rowKey: any, colName: any) => Promise<void>}
     */
    const onCellEdited = useCallback(async (_, newValue, rowKey, colName) => {
        switch (colName) {
            case SalesOrderInformationViewProductsDataGridColumnNames.quantity:
                return updateQuantity(order?.items?.find(e => e.id === rowKey), newValue);
            case SalesOrderInformationViewProductsDataGridColumnNames.backOrderQuantity:
                return updateBackorderQuantity(order?.items?.find(e => e.id === rowKey), newValue);
            case SalesOrderInformationViewProductsDataGridColumnNames.comment:
                return updateItemComment(order?.items?.find(e => e.id === rowKey), newValue);
            default:
                return;
        }
    }, [order?.items, updateItemComment, updateQuantity, updateBackorderQuantity])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return order?.items?.map(item => {
            const isNegative = Number(item.orderQty) < 0;
            const availableQty = productAvailabilities[item.partNo]?.availability?.find(e => e.warehouseCode === item.whse)?.availableQty;

            let statusColor;
            if (item.isSerialized) {
                if (Math.abs(Number(item.orderQty)) === item.serials?.length)
                    statusColor = 'background-green'
                else if (item.serials?.length === 0)
                    statusColor = 'background-red'
                else
                    statusColor = 'background-yellow'
            } else {
                if (Number(item.orderQty) === Number(item.committedQty))
                    statusColor = 'background-green'
                else if (Number(item.committedQty) === 0)
                    statusColor = 'background-red';
                else
                    statusColor = 'background-yellow'
            }

            const quantityEditable = !(
                order?.isClosed ||
                item.partNo === order?.appliedGiftCardInfo?.equalPartNo ||
                (!item?.typeInfo?.code || item?.typeInfo?.code === ProductTypes.nonPhysical)
            )

            const backOrderQuantityEditable = !(
                (!item?.typeInfo?.code || item?.typeInfo?.code === ProductTypes.nonPhysical) ||
                order?.isClosed ||
                item.partNo === order?.appliedGiftCardInfo?.equalPartNo
            ) && !isNegative;
            const commentEditable = EnvService.DEV_showOrderProductComments
                && !order?.isClosed
                && operatorAccessLevels.packages.sales.manageSalesOrders;

            return {
                key: item.id,
                data: item,
                cells: {
                    [SalesOrderInformationViewProductsDataGridColumnNames.remove]:
                        <IconButton
                            onPressed={() => getRemoveConfirmation(item)}
                            disabled={updating}
                            color={'paper.error'}
                            className={'remove-icon'}
                        >
                            {
                                updating
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                            }
                        </IconButton>,
                    [SalesOrderInformationViewProductsDataGridColumnNames.partNo]: <>
                        {
                            !order?.isClosed &&
                            <div className={`sales-order-dot mr-2 ${statusColor}`}/>
                        }
                        <ProductInformationPopover
                            partNo={order?.appliedGiftCardInfo?.equalPartNo === item?.partNo ? null : item?.partNo}
                            wrapperProps={{
                                ...dataGridCellTextProps,
                            }}
                        >
                            {item?.partNo ?? ""}
                        </ProductInformationPopover>
                    </>,
                    [SalesOrderInformationViewProductsDataGridColumnNames.warehouse]: item?.whse ?? '',
                    [SalesOrderInformationViewProductsDataGridColumnNames.description]:
                        <div dangerouslySetInnerHTML={{__html: item?.description ?? ''}}/>,
                    [SalesOrderInformationViewProductsDataGridColumnNames.discount]: `${item.discountPct ?? '0'} %`,
                    [SalesOrderInformationViewProductsDataGridColumnNames.quantity]: {
                        value: ({openEditPopover}) => <>
                            {
                                quantityEditable && operatorAccessLevels.packages.sales.manageSalesOrders &&
                                <IconButton
                                    color={'secondary'}
                                    onPressed={() => openEditPopover()}
                                    disabled={updating}
                                >
                                    {
                                        updating
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                            }
                            <p {...dataGridCellTextProps}
                               className={classnames({'ml-2': quantityEditable && operatorAccessLevels.packages.sales.manageSalesOrders})}>
                                {item.orderQty ?? '0'}
                            </p>
                        </>,
                        editable: quantityEditable && operatorAccessLevels.packages.sales.manageSalesOrders,
                        editableValue: item.orderQty,
                        EditableContentComponent:
                            <QuantityEditor
                                {...(
                                    isNegative
                                        ? {min: Math.min(0, Number(item.orderQty)), max: 0}
                                        : {max: Math.max(0, Number(item.orderQty)), min: 0}
                                )}
                            />,
                    },
                    [SalesOrderInformationViewProductsDataGridColumnNames.backOrderQuantity]: {
                        value: ({openEditPopover}) => <>
                            {
                                backOrderQuantityEditable && operatorAccessLevels.packages.sales.manageSalesOrders &&
                                <IconButton
                                    color={'secondary'}
                                    onPressed={() => openEditPopover()}
                                    disabled={updating}
                                >
                                    {
                                        updating
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                            }
                            <p {...dataGridCellTextProps}
                               className={classnames({'ml-2': backOrderQuantityEditable && operatorAccessLevels.packages.sales.manageSalesOrders})}>
                                {
                                    !item?.typeInfo?.code || item?.typeInfo?.code === ProductTypes.nonPhysical
                                        ? 'N/A'
                                        : item.backorderQty ?? '0'
                                }
                            </p>
                        </>,
                        editable: backOrderQuantityEditable && operatorAccessLevels.packages.sales.manageSalesOrders,
                        editableValue: item.backorderQty,
                        EditableContentComponent:
                            <QuantityEditor
                                {...(
                                    isNegative
                                        ? {min: Math.min(0, (Number(item.orderQty ?? '0') - Number(item?.committedQty ?? '0'))), max: 0}
                                        : {max: Math.max(0, Number(item.orderQty ?? '0') - Number(item?.committedQty ?? '0')), min: 0}
                                )}
                            />,
                    },
                    [SalesOrderInformationViewProductsDataGridColumnNames.unitPrice]: item.unitPriceBeforeDiscount ?? 0,
                    [SalesOrderInformationViewProductsDataGridColumnNames.totalPrice]: (Number(item.unitPrice ?? '0') * (item.orderQty ?? 0)),
                    [SalesOrderInformationViewProductsDataGridColumnNames.availableQuantity]:
                        productAvailabilities[item.partNo]?.loading
                            ? <CircularProgress size={20} color={'secondary'}/>
                            : availableQty ?? '--',
                    [SalesOrderInformationViewProductsDataGridColumnNames.committedQuantity]:
                        (!item?.typeInfo?.code || item?.typeInfo?.code === ProductTypes.nonPhysical)
                            ? item?.typeInfo?.name ?? 'N/A'
                            : Number(item.orderQty) === 0
                                ? '--'
                                : item.isSerialized
                                    ? <SerialsEditor
                                        item={item}
                                        readonly={!!order?.isClosed}
                                        onSerialNoToggled={updateItemSerialNumbers}
                                        isNegative={isNegative}
                                    />
                                    : (order?.isClosed || !operatorAccessLevels.packages.sales.manageSalesOrders)
                                        ? item?.committedQty
                                        : <CommittedQuantityEditor
                                            item={item}
                                            readonly={!!order?.isClosed}
                                            commit={updateItemCommittedQuantity}
                                            availableCount={availableQty}
                                        />,
                    ...(EnvService.DEV_showOrderProductComments && {
                        [SalesOrderInformationViewProductsDataGridColumnNames.comment]:
                            commentEditable
                                ? {
                                    value: ({openEditPopover}) => <>
                                        <IconButton
                                            color={'secondary'}
                                            onPressed={() => openEditPopover()}
                                            disabled={updating}
                                        >
                                            {
                                                updating
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <Edit className={"icon small"}/>
                                            }
                                        </IconButton>
                                        <p {...dataGridCellTextProps} className={'ml-2'}>
                                            {item?.comment?.trim() ? item.comment : '--'}
                                        </p>
                                    </>,
                                    editable: true,
                                    editableValue: item?.comment ?? '',
                                    EditableContentComponent: <CommentEditor/>,
                                }
                                : (item?.comment?.trim() ? item.comment : '--'),
                    }),
                },
            }
        })
    }, [getRemoveConfirmation, operatorAccessLevels.packages.sales.manageSalesOrders, order?.appliedGiftCardInfo?.equalPartNo, order?.isClosed, order?.items, productAvailabilities, updateItemCommittedQuantity, updateItemSerialNumbers, updating])

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center'>
                <Paper className='paper w-100 px-3 py-2'>
                    <Row className='no-gutters align-items-center justify-content-between w-100'>
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            columns={dataGridColumns}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            hideFooter
                            hideExporting
                            visibleRows={10}
                            rows={dataGridRows}
                            state={{
                                loading: {state: orderLoading},
                                density: dataGridDensity,
                            }}
                            onCellEdited={onCellEdited}
                        />
                        <div className='my-2 w-100'/>
                    </Row>
                </Paper>
            </Row>
        </>
    )
}

const QuantityEditor = ({value, setValue, setCanSubmitChanges, min, max}) => {
    const operatorAccessLevels = useAccessLevels();

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        const parsed = Number(value)
        setCanSubmitChanges(!Number.isNaN(parsed));
    }, [setCanSubmitChanges, value])

    /**
     * Changes the value of this editor.
     *
     * - ensures the value is non-negative.
     * @type {(value: any) => void}
     */
    const onValueChanged = useCallback((value) => {
        const parsed = Number(value)
        if (!value || Number.isNaN(parsed))
            return setValue(undefined);
        let _value = parsed;
        if (max !== undefined && max !== null) {
            _value = Math.min(_value, max);
        }
        if (min !== undefined && min !== null) {
            _value = Math.max(_value, min);
        }
        if (!operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders) {
            _value = Math.trunc(_value);
        }
        setValue(_value);
    }, [max, min, setValue, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    return (
        <>
            <OutlinedInput
                fullWidth
                label={"New Quantity"}
                size={'small'}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                type={'number'}
                inputProps={{
                    min: min,
                    max: max,
                }}
            />
        </>
    );
}

const CommittedQuantityEditor = ({
                                     item,
                                     readonly,
                                     commit,
                                     availableQty,
                                 }) => {
    const operatorAccessLevels = useAccessLevels();
    const initialValue = useMemo(() => operatorAccessLevels.packages.sales.issueCredit
            ? Number(item?.committedQty ?? 0)
            : Math.max(Number(item?.committedQty ?? 0), 0)
        , [item?.committedQty, operatorAccessLevels.packages.sales.issueCredit])
    const [value, setValue] = useState(initialValue);
    const [committing, setCommitting] = useState(false);
    const isMounted = useIsMounted();

    const changed = useMemo(() => initialValue !== value, [value, initialValue])
    const canSubmit = useMemo(() => {
        if (!changed)
            return false;
        const parsed = Number(value);
        return !Number.isNaN(parsed);
    }, [changed, value])

    /**
     * With each change in the [initialValue]:
     * - sets the value of the input.
     */
    useLayoutEffect(() => {
        setValue(initialValue);
    }, [initialValue])


    /**
     * Commits the changes of the committed quantity of this item.
     * @return {Promise<void>}
     */
    const commitChanges = useCallback(async () => {
        setCommitting(true);
        await commit(item, value);
        if (!isMounted())
            return;
        setCommitting(false);
    }, [commit, item, value, isMounted])

    /**
     * Changes the value of the input.
     *
     * - ensures that the saved value is within the acceptable range for this item.
     * @param {any} value
     */
    const onValueChanged = useCallback((value) => {
        if (!value || Number.isNaN(Number(value))) {
            return setValue(undefined);
        }
        const orderQty = Number(item.orderQty ?? '0');
        const parsedValue = Number(value ?? '0');
        const values = [
            parsedValue,
            orderQty,
        ];
        const orderNegative = orderQty < 0;
        if (orderNegative && operatorAccessLevels.packages.sales.issueCredit) {
            setValue(Math.min(Math.max(...values), 0));
        } else {
            if ((!!availableQty?.toString()) && !Number.isNaN(Number(availableQty))) {
                values.push(availableQty);
            }
            setValue(Math.max(Math.min(...values), 0))
        }
    }, [availableQty, item?.orderQty, operatorAccessLevels.packages.sales.issueCredit])

    return (
        <>
            <OutlinedInput
                fullWidth={false}
                size={'small'}
                type={'number'}
                disabled={committing || readonly}
                value={value}
                onChange={onValueChanged}
                InputProps={{
                    readOnly: readonly,
                }}
            />
            <SaveOrRollback
                column={false}
                showText={false}
                loading={committing}
                show={{
                    save: canSubmit,
                    cancel: changed,
                }}
                onRollback={() => setValue(initialValue)}
                onSave={commitChanges}
            />
        </>
    );
}

const filter = createFilterOptions();

const SerialsEditor = ({
                           item,
                           onSerialNoToggled,
                           readonly = false,
                           isNegative,
                       }) => {
    const [autoCompleteText, setAutoCompleteText] = useState('');
    const [autoCompleteValue, setAutoCompleteValue] = useState();
    const [autoCompleteSerials, setAutoCompleteSerials] = useState();
    const [negativeValue, setNegativeValue] = useState('');
    const [focused, setFocused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [committing, setCommitting] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    /**
     * Fetches the list of applicable serial numbers of this item form its warehouse.
     * @type {() => Promise<void>}
     */
    const getInventorySerialNumbers = useCallback(async () => {
        setLoading(true);
        const response = await Api.getInventorySerialNumbers(item.whse, item.partNo);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAutoCompleteSerials(response.data);
        }
        setLoading(false);
    }, [item.whse, item.partNo, isMounted])

    /**
     * With each change in the [focused] value of the props:
     * Fetches the applicable serial numbers of this item from the server.
     */
    useEffect(() => {
        if (readonly || !focused || isNegative)
            return;
        void getInventorySerialNumbers();
    }, [focused, getInventorySerialNumbers, readonly, isNegative])

    /**
     * Calls the [onSerialNoToggled] callback of the props and sets the [committing] state.
     * @type {(serialNo: any) => Promise<void>}
     */
    const updateSerialNo = useCallback(async (serialNo) => {
        setCommitting(true);
        await onSerialNoToggled(item, serialNo);
        if (!isMounted())
            return
        setCommitting(false);
    }, [item, isMounted, onSerialNoToggled])

    /**
     * Depending on the type of the text:
     *
     * if null || string => sets the autoComplete text
     * if object => adds the serial number to the list of serialNumbers and then sets the text and value of
     * autoComplete to null
     * @type {(_: any, serialNumber: any) => void}
     */
    const onSelectNewSerialNumber = useCallback((_, serialNumber) => {
        if (!serialNumber) serialNumber = ''
        if (typeof serialNumber === 'string') {
            setAutoCompleteText(serialNumber)
        } else if (typeof serialNumber === 'object') {
            if (!serialNumber.value?.isAvailable) {
                setAutoCompleteValue(null);
                setAutoCompleteText('');
                return;
            }
            updateSerialNo(serialNumber.value).then();
            setAutoCompleteValue(null);
            setAutoCompleteText('');
        }
    }, [updateSerialNo])

    /**
     * Filters the options of the autoComplete so that the selected Serial numbers won't show.
     * @param options {any}
     * @param params {any}
     * @return {[]}
     */
    const filterOptions = useCallback((options, params) => {
        const _options = filter(options, params);
        const res = [];
        const alreadyChosenSerials = item.serials?.map(e => e.serialNumber) ?? [];
        _options.forEach((option) => {
            if (!alreadyChosenSerials.includes(option.value.serialNumber)) {
                res.push(option);
            }
        })
        return res;
    }, [item.serials])

    /**
     * Renders the autocomplete serial numbers.
     * @param option
     * @return {JSX.Element|string}
     */
    const renderAutoCompleteSerial = useCallback((props, option) => {
        if (!option)
            return '';
        if (typeof option === 'string')
            return (
                <p className='mx-2 p-2'>{option}</p>
            );
        if (option?.title === 'totalCount') {
            return (
                <Row className='no-gutters align-items-center w-100 justify-content-between py-2'>
                    <p className='text-left text-sm secondary-color-1 font-weight-bold mr-3'>
                        Total Count
                    </p>
                    <p className='text-left text-sm secondary-color-1 font-weight-bold text-wrap'>
                        {option.value}
                    </p>
                </Row>
            )
        }
        if (!option?.value?.isAvailable) {
            return (
                <Row className='no-gutters align-items-center w-100 justify-content-between py-2'>
                    <div className={'d-flex justify-content-start align-items-center'}>
                        <div
                            className={'rounded-circle background-tertiary d-flex justify-content-center align-items-center mr-2'}
                            style={{width: '20px', height: '20px'}}>
                            <Remove fontSize={'small'} className={'icon extraSmall'}/>
                        </div>

                        <p className='text-left text-sm text-color-1 mr-3'>
                            {option.value?.serialNumber}
                        </p>
                    </div>
                    <p className='text-left text-sm text-wrap'>
                        WHSE: {option.value?.warehouseCode}
                    </p>
                </Row>
            )
        }
        return (
            <Row className='no-gutters align-items-center w-100 justify-content-between py-2'>
                <p className='text-left text-sm text-color-1 mr-3'>
                    {option.value?.serialNumber}
                </p>
                <p className='text-left text-sm text-color-3 text-wrap'>
                    WHSE: {option.value?.warehouseCode}
                </p>
            </Row>
        )
    }, [])

    const chips = useMemo(() => {
        const chipsType = (readonly || !operatorAccessLevels.packages.sales.manageSalesOrders)
            ? ChipsTypes.none
            : committing
                ? ChipsTypes.loading
                : ChipsTypes.remove;
        return item?.serials?.map(serialNumber =>
            <span key={`${serialNumber.serialNumber}`} {...dataGridCellTextProps}>
                <Chips
                    type={chipsType}
                    content={serialNumber.serialNumber}
                    onIconClick={() => updateSerialNo(serialNumber)}
                />
            </span>
        )
    }, [readonly, operatorAccessLevels.packages.sales.manageSalesOrders, committing, item?.serials, updateSerialNo])

    const options = useMemo(() => [
        ...(loading ? [] : [
            {
                title: `totalCount`,
                value: autoCompleteSerials?.filter(e => e.isAvailable)?.length ?? 0,
                disabled: true,
            },
        ]),
        ...(
            autoCompleteSerials?.map(serial => ({
                title: `${serial.serialNumber}`,
                value: serial,
            })) ?? []
        )
    ], [autoCompleteSerials, loading])

    /**
     * Adds the negative serial number to the list of serial numbers
     * @type {(function(): Promise<void>)}
     */
    const addNegativeSerialNumber = useCallback(async () => {
        if (!negativeValue?.trim())
            return;
        const exists = item.serials?.some(e => e.serialNumber === negativeValue?.trim());
        if (exists) {
            setNegativeValue('');
            ToastService.warning({
                message: `This serial number is already added to the list of serial numbers for ${item.partNo}`,
                title: 'Duplicated Serial Number',
            })
            return;
        }
        const serialNo = {
            id: null,
            serialNumber: negativeValue,
            isAvailable: true,
        }
        await updateSerialNo(serialNo);
        setNegativeValue('');
    }, [item.partNo, item.serials, negativeValue, updateSerialNo])

    /**
     * Adds the serial number if the pressed key is "Enter".
     * @type {(function(*): void)|*}
     */
    const commitAddingNegativeSerialNo = useCallback((e) => {
        switch (e.key) {
            case "Enter":
                void addNegativeSerialNumber();
                break;
            default:
                return;
        }
    }, [addNegativeSerialNumber]);


    return (
        <>
            <div className={'d-flex w-100 flex-wrap'} style={{gap: 8}}>
                {chips}
                {
                    !readonly && operatorAccessLevels.packages.sales.manageSalesOrders &&
                    (item?.serials?.length < Math.abs(Number(item.orderQty))) &&
                    <>
                        {
                            committing &&
                            <div className={'loading sales-input'}>
                                <div/>
                            </div>
                        }
                        {
                            !committing &&
                            <>
                                {
                                    isNegative &&
                                    <div className={'d-flex align-items-center'}>
                                        <OutlinedInput
                                            fullWidth={false}
                                            size={'small'}
                                            disabled={committing || readonly}
                                            value={negativeValue?.toString() ?? ''}
                                            onChange={setNegativeValue}
                                            icon={<EnterIcon className={'icon small'}/>}
                                            iconButtonProps={{color: 'paper.secondary', disabled: !negativeValue}}
                                            onKeyUp={commitAddingNegativeSerialNo}
                                            onIconClick={addNegativeSerialNumber}
                                            InputProps={{
                                                readOnly: readonly,
                                            }}
                                        />
                                    </div>
                                }
                                {
                                    !isNegative &&
                                    <OutlinedAutoComplete
                                        size={'small'}
                                        style={{width: '180px'}}
                                        classes={{
                                            popper: 'sales-order-serials-auto-complete-popper',
                                        }}
                                        onFocus={() => setFocused(true)}
                                        value={autoCompleteValue}
                                        loading={loading}
                                        onSelect={onSelectNewSerialNumber}
                                        onChange={(e) => setAutoCompleteText(e.target.value)}
                                        clearOnBlur
                                        freeSolo={false}
                                        type={'text'}
                                        variant={'outlined'}
                                        inputValue={autoCompleteText}
                                        filterOptions={filterOptions}
                                        options={options}
                                        renderOption={renderAutoCompleteSerial}
                                        getOptionDisabled={(option) => !option.value?.isAvailable}
                                        loadingText={
                                            <div className='loading auto-complete'>
                                                <div/>
                                                <div/>
                                                <div/>
                                                <div/>
                                            </div>
                                        }
                                    />
                                }
                            </>
                        }
                    </>
                }
            </div>
        </>
    );
}


const CommentEditor = ({value, setValue, setCanSubmitChanges}) => {
    useLayoutEffect(() => {
        setCanSubmitChanges(true);
    }, [setCanSubmitChanges]);

    const onValueChanged = useCallback((e) => {
        setValue(e.target.value);
    }, [setValue]);

    return (
        <TextField
            fullWidth
            label={"Comment"}
            size={'small'}
            multiline
            minRows={3}
            value={value?.toString() ?? ''}
            onChange={onValueChanged}
            sx={{
                minWidth: 280,
            }}
        />
    );
}


export default SalesOrderInformationViewProductsSection;
